import { jsx } from 'react/jsx-runtime';
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { isAxiosError } from 'axios';
import { del, get, set } from 'idb-keyval';
import { useMemo } from 'react';
import { useConsentSelector } from '@provider/consent';
import { debounce } from '@utils/common';
import { DAYS_TO_MS, HOURS_TO_MS, configRefs } from '@utils/constants';

const tanstackQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1 * HOURS_TO_MS,
      gcTime: 1 * DAYS_TO_MS
    }
  },
  queryCache: new QueryCache({
    onError: (err) => console.error(err)
  })
});
function TanstackQueryProvider(props) {
  const hasConsent = useConsentSelector(
    (state) => state.cookies.necessary && state.cookies.preferences
  );
  const persistOptions = useMemo(() => {
    const tanstackPersister = !hasConsent ? {
      persistClient: () => {
      },
      removeClient: () => {
      },
      restoreClient: () => void 0
    } : createIDBPersister("TANSTACK_QUERY__CACHE");
    return {
      persister: tanstackPersister,
      buster: configRefs.env.version.dangerouslyAssertSyncValue(),
      maxAge: 21 * DAYS_TO_MS,
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => !query.meta || !("persist" in query.meta) || !!query.meta["persist"]
      }
    };
  }, [hasConsent]);
  function revalidateQueriesIfNeeded() {
    var _a;
    for (const query of tanstackQueryClient.getQueryCache().findAll()) {
      if (((_a = query.meta) == null ? void 0 : _a["revalidateOnRefresh"]) === true) {
        tanstackQueryClient.invalidateQueries({
          queryKey: query.queryKey
        });
      }
    }
  }
  return /* @__PURE__ */ jsx(QueryClientProvider, { client: tanstackQueryClient, children: /* @__PURE__ */ jsx(
    PersistQueryClientProvider,
    {
      client: tanstackQueryClient,
      persistOptions,
      onSuccess: revalidateQueriesIfNeeded,
      children: props.children
    }
  ) });
}
function createIDBPersister(idbValidKey) {
  return {
    persistClient: debounce(async (client) => {
      for (const query of client.clientState.queries) {
        if (query.promise) {
          query.promise = void 0;
        }
        if (query.state.error && isAxiosError(query.state.error)) {
          query.state.error = {
            name: query.state.error.name,
            message: query.state.error.message
          };
        }
        if (query.state.fetchFailureReason && isAxiosError(query.state.fetchFailureReason)) {
          query.state.fetchFailureReason = {
            name: query.state.fetchFailureReason.name,
            message: query.state.fetchFailureReason.message
          };
        }
      }
      client.clientState.queries = client.clientState.queries.filter(
        (query) => (!query.meta || !("persist" in query.meta) || !!query.meta["persist"]) && // component entries are set manually in PM2 and as such the
        // meta-data from the query won't be available. As such we exclude it
        // from persisting manually.
        query.queryKey[0] !== "component-framework/get-component-entry"
      );
      await set(idbValidKey, client);
    }, 2e3),
    restoreClient: async () => {
      return await get(idbValidKey);
    },
    removeClient: async () => {
      await del(idbValidKey);
    }
  };
}

export { TanstackQueryProvider, tanstackQueryClient };
//# sourceMappingURL=provider.tanstack-query.js.map
